import { Award, BookOpenCheck, CalendarDays, GraduationCap, Layers } from "lucide-react";

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const normalizeActivities = (activities = []) =>
  activities
    .filter(Boolean)
    .map((item, index) => ({
      id: item._id || item.id || index,
      title: item.title || item.name || `Activity ${index + 1}`,
      description: item.description || "",
      source: item.source || "Refine",
      completedAt: formatDate(item.completedAt),
    }));

const CompletedEducation = ({ activities = [], compact = false }) => {
  const items = normalizeActivities(activities);
  const latest = items.find((item) => item.completedAt);

  return (
    <section className={`rounded-[28px] border border-cyan-100 bg-white shadow-sm overflow-hidden ${compact ? "mt-6" : "mx-4 mt-10"}`}>
      <div className="bg-gradient-to-r from-cyan-50 via-white to-sky-50 px-5 md:px-8 py-6 border-b border-cyan-100">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="font-outfit text-xs uppercase tracking-[0.24em] text-cyan-700 mb-2">
              Refine Progress
            </p>
            <h2 className={`font-cinzel text-gray-950 ${compact ? "text-xl" : "text-3xl md:text-4xl"}`}>
              Completed Education Activities
            </h2>
            <p className="font-outfit text-sm text-gray-600 mt-2 max-w-2xl">
              A record of completed Refine learning, workshops, tutorials, and creative growth milestones.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 min-w-[220px]">
            <div className="rounded-2xl bg-white border border-cyan-100 px-4 py-3">
              <div className="flex items-center gap-2 text-cyan-700">
                <Award size={16} />
                <span className="font-outfit text-xs uppercase tracking-[0.18em]">Total</span>
              </div>
              <p className="font-cinzel text-2xl text-gray-950 mt-1">{items.length}</p>
            </div>
            <div className="rounded-2xl bg-white border border-cyan-100 px-4 py-3">
              <div className="flex items-center gap-2 text-cyan-700">
                <CalendarDays size={16} />
                <span className="font-outfit text-xs uppercase tracking-[0.18em]">Latest</span>
              </div>
              <p className="font-outfit text-sm font-semibold text-gray-950 mt-2">
                {latest?.completedAt || "Not yet"}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-5 md:p-8">
        {items.length > 0 ? (
          <div className={`grid gap-4 ${compact ? "grid-cols-1" : "md:grid-cols-2"}`}>
            {items.map((item, index) => (
              <article
                key={item.id}
                className="group rounded-2xl border border-gray-200 bg-slate-50 p-5 hover:bg-white hover:border-cyan-200 hover:shadow-md transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#B6FCFF] to-[#00C3FF] flex items-center justify-center flex-shrink-0 shadow-sm">
                    <BookOpenCheck size={20} className="text-gray-950" strokeWidth={2} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="inline-flex items-center gap-1 rounded-full bg-white border border-cyan-100 px-2.5 py-1 text-[11px] font-outfit font-semibold text-cyan-700">
                        <Layers size={12} />
                        {item.source}
                      </span>
                      {item.completedAt && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-white border border-gray-200 px-2.5 py-1 text-[11px] font-outfit font-semibold text-gray-500">
                          <CalendarDays size={12} />
                          {item.completedAt}
                        </span>
                      )}
                    </div>
                    <h3 className="font-cinzel text-lg text-gray-950 leading-snug">
                      {item.title}
                    </h3>
                    {item.description && (
                      <p className="font-outfit text-sm leading-6 text-gray-600 mt-2">
                        {item.description}
                      </p>
                    )}
                  </div>
                  <span className="font-cinzel text-sm text-gray-300 group-hover:text-cyan-500 transition-colors">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="rounded-3xl border border-dashed border-cyan-200 bg-cyan-50/40 px-6 py-10 text-center">
            <div className="mx-auto mb-4 w-14 h-14 rounded-2xl bg-white border border-cyan-100 flex items-center justify-center">
              <GraduationCap size={26} className="text-cyan-600" />
            </div>
            <h3 className="font-cinzel text-xl text-gray-950 mb-2">No completed activities yet</h3>
            <p className="font-outfit text-sm text-gray-600 max-w-md mx-auto">
              Completed Refine work will appear here after an admin records workshops, tutorials, or learning milestones for this member.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default CompletedEducation;
